import { Option } from "@cartographerio/fp";
import { isArray, isBoolean } from "@cartographerio/guard";
import { MrsHorizontalOrientationEnum, MrsRiverWoodArtificialElementEnum, MrsRiverWoodBarLocationEnum, MrsRiverWoodFeatureLocationEnum, MrsRiverWoodHydraulicTypeEnum, MrsRiverWoodRetainedByArtificialFeatureEnum, MrsRiverWoodRetainedByNaturalFeatureEnum, MrsRiverWoodTypeEnum, MrsUadEnum, UrsSurveyBankEnum, } from "@cartographerio/inventory-enums";
import { attachmentField, checkbox, columns2, columns3, dynamicValue, form, integerField, minValue, multiSelect, numberField, otherSpecify, page, pointField, required, requiredIfV2, section, select, selectOption, subsection, subsubsection, text, textArea, textField, timestampField, topoExpr, vstack, } from "@cartographerio/topo-form";
import { checkExhausted } from "@cartographerio/util";
import { outdent } from "outdent";
import { narrowOtherSpecify } from "../ArrtWaterQuality/form.js";
function flowChartOptions(noLabel, yesLabel) {
    return [
        selectOption(false, `No - ${noLabel}`),
        selectOption(true, `Yes - ${yesLabel}`),
    ];
}
function narrow(...blocks) {
    return columns2(vstack(...blocks));
}
const flowChartYN = (env, question) => env
    .getAbsoluteAs(["data", "character", question], isBoolean)
    .map(b => (b ? "y" : "n"))
    .getOrNull();
const woodType = topoExpr(env => env
    .getAbsoluteAs(["data", "character", "woodType"], MrsRiverWoodTypeEnum.isValue)
    .getOrNull());
const hydraulicTypeOverride = topoExpr(env => env
    .getAbsoluteAs(["data", "character", "hydraulicTypeOverride"], MrsRiverWoodHydraulicTypeEnum.isValue)
    .getOrNull());
const isolatedTreeVisible = topoExpr((_env, run) => run(woodType) === MrsRiverWoodTypeEnum.Tree);
const q1Visible = topoExpr((_env, run) => run(woodType) === MrsRiverWoodTypeEnum.Tree);
const q2Visible = topoExpr((env, run) => run(q1Visible) && flowChartYN(env, "isolatedTreeLeaningTree") === "y");
const q3Visible = topoExpr((env, run) => run(q2Visible) && flowChartYN(env, "isolatedTreeFallenTree") === "y");
const woodAccumulationVisible = topoExpr((_env, run) => run(woodType) === MrsRiverWoodTypeEnum.Wood || run(q5Visible));
const q4Visible = topoExpr((_env, run) => run(woodType) === MrsRiverWoodTypeEnum.Wood);
const q5Visible = topoExpr((env, run) => (run(q3Visible) && flowChartYN(env, "isolatedTreeHighJam") === "n") ||
    (run(q4Visible) && flowChartYN(env, "woodAccumulationHighJam") === "n"));
const q6Visible = topoExpr((env, run) => run(q5Visible) && flowChartYN(env, "woodAccumulationPartialJam") === "y");
const calculatedHydraulicType = topoExpr((env, run) => {
    function test(test, branches) {
        return test == null ? null : branches[test]();
    }
    const calcQ5Type = () => test(flowChartYN(env, "woodAccumulationPartialJam"), {
        n: () => MrsRiverWoodHydraulicTypeEnum.PartialJam,
        y: () => test(flowChartYN(env, "woodAccumulationCompleteJam"), {
            n: () => MrsRiverWoodHydraulicTypeEnum.CompleteJam,
            y: () => MrsRiverWoodHydraulicTypeEnum.ActiveJam,
        }),
    });
    const hydraulicType = test(run(woodType), {
        [MrsRiverWoodTypeEnum.Tree]: () => test(flowChartYN(env, "isolatedTreeLeaningTree"), {
            n: () => MrsRiverWoodHydraulicTypeEnum.LeaningTree,
            y: () => test(flowChartYN(env, "isolatedTreeFallenTree"), {
                n: () => MrsRiverWoodHydraulicTypeEnum.FallenTree,
                y: () => test(flowChartYN(env, "isolatedTreeHighJam"), {
                    y: () => MrsRiverWoodHydraulicTypeEnum.HighJam,
                    n: calcQ5Type,
                }),
            }),
        }),
        [MrsRiverWoodTypeEnum.Wood]: () => test(flowChartYN(env, "woodAccumulationHighJam"), {
            y: () => MrsRiverWoodHydraulicTypeEnum.HighJam,
            n: calcQ5Type,
        }),
    });
    return hydraulicType;
});
const finalHydraulicType = topoExpr((_env, run) => {
    return Option.wrap(run(hydraulicTypeOverride))
        .orElseNullable(() => run(calculatedHydraulicType))
        .getOrNull();
});
export default form({
    title: "RiverWood",
    pages: [
        page({
            title: "Page 1",
            path: ["data"],
            blocks: [
                section({
                    title: "1. General Information",
                    path: [],
                    blocks: [
                        subsection({
                            title: "1.1 Survey Time and Conditions",
                            path: [],
                            blocks: [
                                narrow(timestampField({
                                    label: "Survey Date/Time",
                                    path: ["generalInformation", "recorded"],
                                    required: required(),
                                    defaultValue: "now",
                                    help: outdent `
                      If you are unsure of the exact date,
                      choose the first day of the relevant month e.g. 01/05/2023.
                      If you are unsure of the time, you can change this to 12:00.
                    `,
                                }), checkbox({
                                    label: "Bed Visible?",
                                    checkboxLabel: "Is the bed fully or partially visible?",
                                    path: ["generalInformation", "bedVisible"],
                                    defaultValue: false,
                                }), checkbox({
                                    label: "Adverse Conditions?",
                                    checkboxLabel: "Are there adverse conditions at the time of the survey?",
                                    path: ["generalInformation", "adverseConditions"],
                                    defaultValue: false,
                                    help: outdent `
                      Examples of adverse conditions include: limited access, high river flows,
                      impenetrable riparian vegetation, and poor light conditions.
                    `,
                                }), otherSpecify({
                                    label: "Adverse Conditions Notes",
                                    thatPath: ["generalInformation", "adverseConditions"],
                                    thisPath: ["generalInformation", "adverseConditionsNotes"],
                                    rows: 3,
                                    level: "error",
                                    test: other => other === true,
                                    visible: "auto",
                                    help: "Describe the adverse conditions.",
                                }), select({
                                    label: "Survey Conducted from Which Bank?",
                                    path: ["generalInformation", "surveyBank"],
                                    options: UrsSurveyBankEnum.entries,
                                    required: required("info"),
                                    help: `"Left" and "right" refer to the banks as you face in a downstream direction.`,
                                })),
                            ],
                        }),
                        subsection({
                            title: "1.2 Project Name and Survey Location",
                            path: [],
                            blocks: [
                                narrow(textField({
                                    label: "Project Name",
                                    path: ["generalInformation", "projectName"],
                                    help: "Name of the restoration project being monitored (if applicable).",
                                }), textField({
                                    label: "River Name",
                                    path: ["generalInformation", "riverName"],
                                    help: outdent `
                      Name of the river or stream (e.g. as shown on 1:50,000 scale maps).

                      Unnamed tributaries should be named with reference to a main watercourse
                      (e.g. "Tributary of River Brent").
                    `,
                                }), textField({
                                    label: "Reach Name",
                                    path: ["generalInformation", "reachName"],
                                    help: "Name of the reach in which the jam is located.",
                                }), textField({
                                    label: "Wood Number / Identifier",
                                    path: ["generalInformation", "woodNumber"],
                                    help: "Reference code/number for the jam.",
                                })),
                            ],
                        }),
                        subsection({
                            title: "1.3 Photographs",
                            path: [],
                            blocks: [
                                attachmentField({
                                    label: null,
                                    path: ["generalInformation", "photographs"],
                                    maxFiles: 3,
                                    help: outdent `
                  Upload 3 photos as follows:

                  - View across the channel at the jam site
                  - The jam viewed from downstream
                  - The jam viewed from upstream
                  `,
                                }),
                            ],
                        }),
                        subsection({
                            title: "1.4 Occasional/Optional Survey Sections Completed?",
                            path: [],
                            blocks: [
                                narrow(checkbox({
                                    label: null,
                                    checkboxLabel: "Section 3 completed?",
                                    path: ["generalInformation", "section3Completed"],
                                }), checkbox({
                                    label: null,
                                    checkboxLabel: "Section 4 completed?",
                                    path: ["generalInformation", "section4Completed"],
                                }), checkbox({
                                    label: null,
                                    checkboxLabel: "Section 5 completed?",
                                    path: ["generalInformation", "section5Completed"],
                                })),
                            ],
                        }),
                    ],
                }),
                section({
                    title: "2 Wood Character and Hydraulic Type",
                    path: [],
                    blocks: [
                        subsection({
                            title: "2.1 Wood Location",
                            path: [],
                            blocks: [
                                pointField({
                                    label: "Wood Location",
                                    path: ["character", "woodLocation"],
                                    help: "Location of the centre of the jam.",
                                }),
                            ],
                        }),
                        subsection({
                            title: "2.2 Beavers?",
                            path: [],
                            blocks: [
                                narrow(checkbox({
                                    label: "Wood Cut By Beavers?",
                                    checkboxLabel: "Was the jam wood cut by beavers?",
                                    help: "i.e. the ends of wood pieces should show beaver tooth marks",
                                    path: ["character", "cutByBeavers"],
                                    defaultValue: false,
                                }), checkbox({
                                    label: "Evidence of Beavers",
                                    checkboxLabel: "Is there any other evidence of the presence of beavers?",
                                    path: ["character", "presenceOfBeavers"],
                                    defaultValue: false,
                                }), otherSpecify({
                                    label: "Other Evidence of Beavers",
                                    thatPath: ["character", "presenceOfBeavers"],
                                    thisPath: ["character", "evidenceOfBeavers"],
                                    rows: 3,
                                    level: "error",
                                    test: other => other === true,
                                    visible: "auto",
                                    help: "Describe the other evidence.",
                                })),
                            ],
                        }),
                        subsection({
                            title: "2.3 Artificial Elements?",
                            path: [],
                            blocks: [
                                narrow(multiSelect({
                                    label: "Artificial Elements",
                                    path: ["character", "artificialElements", "selected"],
                                    options: MrsRiverWoodArtificialElementEnum.entries,
                                    appearance: "checkboxgroup",
                                }), otherSpecify({
                                    label: "Other Artificial Element",
                                    basePath: ["character", "artificialElements"],
                                    rows: 3,
                                    level: "error",
                                    test: value => isArray(value) &&
                                        value.includes(MrsRiverWoodArtificialElementEnum.Other),
                                    visible: "auto",
                                    help: "Describe the other artificial element(s).",
                                })),
                            ],
                        }),
                        subsection({
                            title: "2.4 Hydraulic Type of Isolated Tree / Wood Accumulation",
                            path: [],
                            help: outdent `
                For *Type of Wood* below, select 'Individual isolated tree' or 'Wood Accumulation' as appropriate
                and then answer Yes/No to the questions in numerical order.

                Each question directs you to the next question until you reach an hydraulic type,
                which is displayed at the end of the section.
              `,
                            blocks: [
                                narrow(select({
                                    label: "Type of Wood",
                                    help: "e.g. leaning tree, fallen tree, partial jam, complete jam, active jam, high jam",
                                    path: ["character", "woodType"],
                                    options: MrsRiverWoodTypeEnum.entries,
                                    required: required(),
                                })),
                                subsubsection({
                                    title: "Individual Isolated Tree",
                                    help: "> 2m tall and > 10cm diameter, leaning over part of the bankfull channel",
                                    path: [],
                                    visible: isolatedTreeVisible,
                                    blocks: [
                                        narrow(select({
                                            label: "Q1: Is the isolated tree leaning at <30° above the horizontal?",
                                            path: ["character", "isolatedTreeLeaningTree"],
                                            options: flowChartOptions(MrsRiverWoodHydraulicTypeEnum.labelOf("LT"), "Go to Q2"),
                                            visible: q1Visible,
                                            customRules: [
                                                requiredIfV2({
                                                    message: "You must specify a value.",
                                                    otherTest: q1Visible,
                                                }),
                                            ],
                                        }), select({
                                            label: "Q2: Is any part of the trunk of the tree located within the bankfull channel?",
                                            path: ["character", "isolatedTreeFallenTree"],
                                            options: flowChartOptions(MrsRiverWoodHydraulicTypeEnum.labelOf("FT"), "Go to Q3"),
                                            visible: q2Visible,
                                            customRules: [
                                                requiredIfV2({
                                                    message: "You must specify a value.",
                                                    otherTest: q2Visible,
                                                }),
                                            ],
                                        }), select({
                                            label: "Q3: Is the tree trunk suspended above the stream bed so that water can flow freely beneath it during low stream flows?",
                                            path: ["character", "isolatedTreeHighJam"],
                                            options: flowChartOptions("Go to Q5", MrsRiverWoodHydraulicTypeEnum.labelOf("HJ")),
                                            visible: q3Visible,
                                            customRules: [
                                                requiredIfV2({
                                                    message: "You must specify a value.",
                                                    otherTest: q3Visible,
                                                }),
                                            ],
                                        })),
                                    ],
                                }),
                                subsubsection({
                                    title: "Wood Accumulation",
                                    help: "At least two pieces of wood > 1m long and 10cm diameter (including complete trees) located at least partly within the bankfull channel",
                                    path: [],
                                    visible: woodAccumulationVisible,
                                    blocks: [
                                        narrow(select({
                                            label: "Q4: Is the wood suspended above the stream bed so that water can flow freely beneath it during low stream flows?",
                                            path: ["character", "woodAccumulationHighJam"],
                                            options: flowChartOptions("Go to Q5", MrsRiverWoodHydraulicTypeEnum.labelOf("HJ")),
                                            visible: q4Visible,
                                            customRules: [
                                                requiredIfV2({
                                                    message: "You must specify a value.",
                                                    otherTest: q4Visible,
                                                }),
                                            ],
                                        }), select({
                                            label: "Q5: Is the wood in contact with the channel bed across the entire width of the stream bed?",
                                            path: ["character", "woodAccumulationPartialJam"],
                                            options: flowChartOptions(MrsRiverWoodHydraulicTypeEnum.labelOf("PJ"), "Go to Q6"),
                                            visible: q5Visible,
                                            customRules: [
                                                requiredIfV2({
                                                    message: "You must specify a value.",
                                                    otherTest: q5Visible,
                                                }),
                                            ],
                                        }), select({
                                            label: "Q6: Is there a clear drop in the water surface level from upstream to downstream of the wood accumulation?",
                                            path: ["character", "woodAccumulationCompleteJam"],
                                            options: flowChartOptions(MrsRiverWoodHydraulicTypeEnum.labelOf("CJ"), MrsRiverWoodHydraulicTypeEnum.labelOf("AJ")),
                                            visible: q6Visible,
                                            customRules: [
                                                requiredIfV2({
                                                    message: "You must specify a value.",
                                                    otherTest: q6Visible,
                                                }),
                                            ],
                                        })),
                                    ],
                                }),
                            ],
                        }),
                        subsubsection({
                            title: "Wood Jam Hydraulic Type",
                            path: [],
                            blocks: [
                                narrow(dynamicValue({
                                    label: "Calculated Hydraulic Type",
                                    value: topoExpr((_env, run) => Option.wrap(run(calculatedHydraulicType))
                                        .map(MrsRiverWoodHydraulicTypeEnum.labelOf)
                                        .getOrNull()),
                                    valueType: "string",
                                }), select({
                                    label: "Hydraulic Type Override",
                                    path: ["character", "hydraulicTypeOverride"],
                                    options: MrsRiverWoodHydraulicTypeEnum.entries,
                                    placeholder: "No override",
                                }), dynamicValue({
                                    label: "Final Hydraulic Type",
                                    value: topoExpr((_env, run) => Option.wrap(run(finalHydraulicType))
                                        .map(MrsRiverWoodHydraulicTypeEnum.labelOf)
                                        .getOrNull()),
                                    valueType: "string",
                                })),
                            ],
                        }),
                    ],
                }),
                section({
                    title: "3 River Channel Dimensions (Occasional)",
                    path: [],
                    help: outdent `
            These measurements are not necessary at every site
            but should be taken occasionally (e.g. every ~500m)
            at a free flowing site near to but not affected by the wood recorded in section 2.
          `,
                    blocks: [
                        pointField({
                            label: "Measurement Location",
                            path: ["channelDimensions", "riverLocation"],
                        }),
                        text({
                            markdown: outdent `
                The figure below illustrates the channel dimensions as seen looking downstream:

                ![Channel Dimensions](https://media.cartographer.io/static/images/mrs-morph/channel-dimensions.png)
              `,
                        }),
                        narrow(numberField({
                            label: "MoRPh River Width",
                            help: "Nearest 0.1m",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["channelDimensions", "morphRiverWidth"],
                        })),
                        columns2(numberField({
                            label: "Left Bank Height",
                            help: "Nearest 0.1m",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["channelDimensions", "leftBankHeight"],
                        }), numberField({
                            label: "Right Bank Height",
                            help: "Nearest 0.1m",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["channelDimensions", "rightBankHeight"],
                        })),
                        narrow(numberField({
                            label: "Bankfull Width",
                            help: "Nearest 0.1m",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["channelDimensions", "bankfullWidth"],
                        })),
                        columns2(numberField({
                            label: "Water Width",
                            help: "Nearest 0.1m",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["channelDimensions", "waterWidth"],
                        }), numberField({
                            label: "Water Depth",
                            help: "Nearest 0.1m",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["channelDimensions", "waterDepth"],
                        })),
                    ],
                }),
                section({
                    title: "4 Jam Anchorage and Adjacent Physical Features (Optional)",
                    path: [],
                    blocks: [
                        subsection({
                            title: "4.1 Anchorage",
                            help: "Record the main feature(s) retaining the wood",
                            path: [],
                            blocks: [
                                multiSelect({
                                    label: "Wood Retained by an Artificial Feature?",
                                    path: ["anchorage", "artificialFeatures", "selected"],
                                    options: MrsRiverWoodRetainedByArtificialFeatureEnum.entries,
                                    appearance: "checkboxgroup",
                                }),
                                narrowOtherSpecify({
                                    label: "Other Artificial Structure",
                                    basePath: ["anchorage", "artificialFeatures"],
                                    level: "error",
                                    test: value => isArray(value) &&
                                        value.includes(MrsRiverWoodArtificialElementEnum.Other),
                                    visible: "auto",
                                    help: "If you selected 'other', please describe.",
                                }),
                                multiSelect({
                                    label: "Wood Retained by a Natural Feature?",
                                    path: ["anchorage", "naturalFeatures"],
                                    options: MrsRiverWoodRetainedByNaturalFeatureEnum.entries,
                                    appearance: "checkboxgroup",
                                }),
                            ],
                        }),
                        subsection({
                            title: "4.2 Adjacent Physical Features",
                            help: outdent `
                Within 1 MoRPH river width upstream/downstream of the wood jam.

                Record the number and location of 'pool, riffle, island, side channel' features.
                Record the location of bars, berms, benches, eroding banks, undercut banks.
                Location can be Upstream, At/under/adjacent or Downstream of the wood.
              `,
                            path: [],
                            blocks: [
                                uadCounts("Pool", ["anchorage", "pool"]),
                                uadCounts("Riffle", ["anchorage", "riffle"]),
                                uadCounts("Island", ["anchorage", "island"]),
                                uadCounts("Side Channel", ["anchorage", "sideChannel"]),
                                uadSelects("Unvegetated Bar", ["anchorage", "unvegetatedBar"], MrsRiverWoodBarLocationEnum.entries, MrsRiverWoodBarLocationEnum.Absent),
                                uadSelects("Vegetated Bar", ["anchorage", "vegetatedBar"], MrsRiverWoodBarLocationEnum.entries, MrsRiverWoodBarLocationEnum.Absent),
                                uadSelects("Berm / Bench", ["anchorage", "bermOrBench"], MrsRiverWoodFeatureLocationEnum.entries, MrsRiverWoodFeatureLocationEnum.Absent),
                                uadSelects("Eroding Bank Face", ["anchorage", "erodingBankFace"], MrsRiverWoodFeatureLocationEnum.entries, MrsRiverWoodFeatureLocationEnum.Absent),
                                uadSelects("Undercut Bank Face", ["anchorage", "undercutBankFace"], MrsRiverWoodFeatureLocationEnum.entries, MrsRiverWoodFeatureLocationEnum.Absent),
                            ],
                        }),
                    ],
                }),
                section({
                    title: "5 Jam Dimensions (Optional)",
                    help: "Measurements to the nearest 0.1m",
                    path: [],
                    blocks: [
                        narrow(numberField({
                            label: "MoRPH River Width at Jam",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "morphRiverWidth"],
                        }), numberField({
                            label: "Width of Jam Across Channel",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "acrossChannelWidth"],
                        }), numberField({
                            label: "Length of Jam Upstream-Downstream",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "jamLength"],
                        }), numberField({
                            label: "Maximum Height of Jam Above Channel Bed",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "jamMaxHeightAboveChannelBed"],
                        }), numberField({
                            label: "Height of Bottom of Jam Above Channel Bed",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "bottomOfJamHeightAboveChannelBed"],
                        }), select({
                            label: "Horizontal Orientation of Jam WRT Channel Banks",
                            path: ["dimensions", "horizontalOrientationOfJam"],
                            options: MrsHorizontalOrientationEnum.entries,
                        }), numberField({
                            label: "Length of Largest Wood Piece in Jam",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "largestWoodPieceLength"],
                        }), numberField({
                            label: "Diameter of Largest Wood Piece in Jam",
                            units: "m",
                            decimalPlaces: 1,
                            path: ["dimensions", "largestWoodPieceDiameter"],
                        })),
                    ],
                }),
                section({
                    title: "Notes",
                    path: [],
                    blocks: [
                        textArea({
                            label: null,
                            path: ["notes"],
                            rows: 5,
                        }),
                    ],
                }),
            ],
        }),
    ],
});
function uadCounts(title, path) {
    return subsubsection({
        title: title,
        path,
        blocks: [
            columns3(...MrsUadEnum.values.map(value => integerField({
                label: formatUadLabel(value),
                path: [value],
                bounds: minValue(0),
                units: "count",
                defaultValue: 0,
            }))),
        ],
    });
}
function uadSelects(title, path, options, defaultValue) {
    return subsubsection({
        title,
        path,
        blocks: [
            columns3(...MrsUadEnum.values.map(value => select({
                label: formatUadLabel(value),
                path: [value],
                options,
                defaultValue,
            }))),
        ],
    });
}
function formatUadLabel(value) {
    switch (value) {
        case "U":
            return "Upstream of Wood";
        case "D":
            return "At / Adjacent to Wood";
        case "A":
            return "Downstream of Wood";
        default:
            return checkExhausted(value);
    }
}
//# sourceMappingURL=form.js.map